import { FormEvent, ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import AuthSplit from "layouts/auth/types/AuthSplit";
import { Button } from "components/ui/Button";
import env from "variables/config";
import { AuthDivider, AuthHeading, EMAIL_RE, Field, FormAlert, GoogleButton, linkClass, PasswordField, storeSession } from "views/auth/authParts";

// Remembered so a returning user only has to type their password.
const LAST_EMAIL_KEY = "lastLoginEmail";

const readLastEmail = () => {
    try {
        return localStorage.getItem(LAST_EMAIL_KEY) ?? "";
    } catch {
        return "";
    }
};

const GOOGLE_ERROR = "Connexion avec Google impossible. Réessayez, ou utilisez votre email.";

function SignInDefault() {
    const navigate = useNavigate();
    const [email, setEmail] = useState(readLastEmail);
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [formError, setFormError] = useState<ReactNode>(null);

    const login = (e: FormEvent) => {
        e.preventDefault();
        const normalizedEmail = email.trim().toLowerCase();
        const nextEmailError = !normalizedEmail
            ? "Saisissez votre email professionnel."
            : !EMAIL_RE.test(normalizedEmail)
            ? "Cet email ne semble pas valide."
            : null;
        const nextPasswordError = password ? null : "Saisissez votre mot de passe.";
        setEmailError(nextEmailError);
        setPasswordError(nextPasswordError);
        setFormError(null);
        if (nextEmailError || nextPasswordError) return;

        setLoading(true);
        axios
            .post(`${env.apiUrl}/auth/login`, { email: normalizedEmail, password })
            .then((response: any) => {
                storeSession(response.data);
                try {
                    localStorage.setItem(LAST_EMAIL_KEY, normalizedEmail);
                } catch {}
                navigate("/");
            })
            .catch((error: any) => {
                const status = error?.response?.status;
                setFormError(
                    status && status < 500
                        ? "Email ou mot de passe incorrect. Vérifiez vos identifiants ou réinitialisez votre mot de passe."
                        : "Connexion impossible pour le moment. Réessayez dans quelques instants."
                );
            })
            .finally(() => setLoading(false));
    };

    const loginGoogle = (token: string) => {
        setFormError(null);
        axios
            .post(`${env.apiUrl}/auth/google`, { token })
            .then((response: any) => {
                if (!response.data.token) throw new Error("No token");
                storeSession(response.data);
                localStorage.setItem("accessCodeValidated", String(response.data.accessCodeValidated !== false));
                navigate(response.data.accessCodeValidated === false ? "/auth/access-code" : "/");
            })
            .catch((error: any) => {
                if (error?.response?.data?.code === "GOOGLE_NO_ACCOUNT") {
                    window.location.href = "http://duckflow.io/contact";
                } else {
                    setFormError(GOOGLE_ERROR);
                }
            });
    };

    return (
        <AuthSplit>
            <AuthHeading title="Bon retour parmi nous" subtitle="Connectez-vous pour retrouver vos signaux du jour." />

            <GoogleButton onToken={loginGoogle} onError={() => setFormError(GOOGLE_ERROR)} />

            <AuthDivider />

            {formError && <FormAlert>{formError}</FormAlert>}

            <form onSubmit={login} noValidate className="space-y-4">
                <Field
                    id="email"
                    label="Email professionnel"
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    autoFocus={!email}
                    placeholder="prenom@entreprise.fr"
                    value={email}
                    error={emailError}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError(null);
                        setFormError(null);
                    }}
                />

                <PasswordField
                    id="password"
                    label="Mot de passe"
                    autoComplete="current-password"
                    autoFocus={!!email}
                    placeholder="Votre mot de passe"
                    value={password}
                    error={passwordError}
                    labelAside={
                        <Link to="/auth/forgot-password" className={`text-sm ${linkClass}`}>
                            Mot de passe oublié ?
                        </Link>
                    }
                    onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError(null);
                        setFormError(null);
                    }}
                />

                <Button type="submit" variant="accent" size="lg" disabled={loading} aria-busy={loading} className="w-full">
                    {loading ? (
                        <>
                            <Loader2 className="h-4 w-4 motion-safe:animate-spin" />
                            Connexion…
                        </>
                    ) : (
                        <>
                            Se connecter
                            <ArrowRight className="h-4 w-4" />
                        </>
                    )}
                </Button>
            </form>
        </AuthSplit>
    );
}

export default SignInDefault;
